import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import certifiedTrainerService from '../../services/certifiedTrainer';
import { CertifiedTrainerDashboard, Course } from '../../types';
import { useToast } from '../../hooks/useToast';

export default function TeachingDashboard() {
  const { t } = useTranslation('teaching');
  const navigate = useNavigate();
  const toast = useToast();
  const [stats, setStats] = useState<CertifiedTrainerDashboard | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [dashboardData, coursesData] = await Promise.all([
        certifiedTrainerService.getDashboard(),
        certifiedTrainerService.getCourses(),
      ]);
      setStats(dashboardData);
      setCourses(coursesData);
    } catch (err: any) {
      setError(err.response?.data?.error || t('dashboard.failedToLoadDashboard'));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCourse() {
    setCreating(true);
    try {
      const newCourse = await certifiedTrainerService.createCourse({
        title: t('courses.untitledCourse'),
        slug: 'untitled-course-' + Date.now(),
        description: '',
        status: 'draft',
      });
      navigate(`/my-teaching/courses/${newCourse._id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('dashboard.failedToCreateCourse'));
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <button
          onClick={handleCreateCourse}
          disabled={creating}
          className="btn-gold px-4 py-2"
        >
          {creating ? t('dashboard.creating') : t('dashboard.createNewCourse')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
        <div className="glass-card rounded-lg p-6">
          <div className="text-3xl font-bold text-gray-900">{stats?.totalCourses || 0}</div>
          <div className="text-gray-500">{t('dashboard.totalCourses')}</div>
        </div>
        <div className="glass-card rounded-lg p-6">
          <div className="text-3xl font-bold text-green-600">{stats?.publishedCourses || 0}</div>
          <div className="text-gray-500">{t('dashboard.publishedCourses')}</div>
        </div>
        <div className="glass-card rounded-lg p-6">
          <div className="text-3xl font-bold text-gold">{stats?.totalStudents || 0}</div>
          <div className="text-gray-500">{t('dashboard.totalMembers')}</div>
        </div>
        <Link to="/my-teaching/approvals" className="glass-card rounded-lg p-6 hover:ring-2 hover:ring-gold/30 transition-all">
          <div className="text-3xl font-bold text-yellow-400">{stats?.pendingEnrollments || 0}</div>
          <div className="text-gray-500">{t('dashboard.pendingEnrollments')}</div>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link to="/my-teaching/exams" className="glass-card rounded-lg p-6 hover:ring-2 hover:ring-gold/30 transition-all">
          <div className="text-3xl font-bold text-gray-900">{stats?.totalExams || 0}</div>
          <div className="text-gray-500">{t('dashboard.exams', { published: stats?.publishedExams || 0 })}</div>
        </Link>
        <Link to="/my-teaching/promo-codes" className="glass-card rounded-lg p-6 hover:ring-2 hover:ring-gold/30 transition-all">
          <div className="text-3xl font-bold text-purple-600">{stats?.activePromoCodes || 0}</div>
          <div className="text-gray-500">{t('dashboard.activePromoCodes')}</div>
        </Link>
      </div>

      {/* Course Status Summary */}
      {(stats?.pendingCourses || 0) + (stats?.draftCourses || 0) > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <h3 className="font-medium text-yellow-700 mb-2">{t('dashboard.actionRequired')}</h3>
          <div className="flex gap-4 text-sm">
            {stats?.draftCourses ? (
              <span className="text-yellow-700">
                {t('dashboard.draftCoursesNeedReview', { count: stats.draftCourses })}
              </span>
            ) : null}
            {stats?.pendingCourses ? (
              <span className="text-yellow-700">
                {t('dashboard.coursesWaitingApproval', { count: stats.pendingCourses })}
              </span>
            ) : null}
          </div>
        </div>
      )}

      {/* Recent Courses */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('dashboard.yourCourses')}</h2>
          <Link to="/my-teaching/courses" className="text-gold hover:underline">
            {t('dashboard.viewAll')}
          </Link>
        </div>

        {courses.length === 0 ? (
          <div className="glass-card rounded-lg p-8 text-center">
            <p className="text-gray-500 mb-4">{t('dashboard.noCoursesYet')}</p>
            <button
              onClick={handleCreateCourse}
              disabled={creating}
              className="text-gold hover:underline"
            >
              {creating ? t('dashboard.creating') : t('dashboard.createFirstCourse')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 6).map((course) => (
              <div
                key={course._id}
                className="glass-card rounded-lg overflow-hidden"
              >
                {course.thumbnail ? (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">{t('dashboard.noThumbnail')}</span>
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 truncate">{course.title}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        course.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : course.status === 'approved'
                          ? 'bg-gold/20 text-gold'
                          : course.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : course.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {course.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mb-3">
                    <span>{t('dashboard.lessons', { count: course.lessonCount || 0 })}</span>
                    <span>{t('dashboard.members', { count: (course as any).activeStudents || 0 })}</span>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to={`/my-teaching/courses/${course._id}`}
                      className="flex-1 text-center px-3 py-2 border border-gray-200 rounded text-sm text-gray-600 hover:bg-gray-100"
                    >
                      {t('dashboard.edit')}
                    </Link>
                    {course.status === 'published' && (
                      <Link
                        to={`/my-teaching/courses/${course._id}/students`}
                        className="flex-1 text-center px-3 py-2 btn-gold rounded text-sm"
                      >
                        {t('dashboard.membersButton')}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <button
          onClick={handleCreateCourse}
          disabled={creating}
          className="block p-6 glass-card rounded-lg text-left"
        >
          <div className="text-2xl mb-2">&#10133;</div>
          <h3 className="font-semibold text-gray-900">{t('dashboard.createNewCourse')}</h3>
          <p className="text-gray-500 text-sm">{t('dashboard.startBuildingCourse')}</p>
        </button>
        <Link
          to="/my-teaching/courses"
          className="block p-6 glass-card rounded-lg"
        >
          <div className="text-2xl mb-2">&#128218;</div>
          <h3 className="font-semibold text-gray-900">{t('dashboard.manageCourses')}</h3>
          <p className="text-gray-500 text-sm">{t('dashboard.manageCoursesDesc')}</p>
        </Link>
        <Link
          to="/my-teaching/exams"
          className="block p-6 glass-card rounded-lg"
        >
          <div className="text-2xl mb-2">&#128203;</div>
          <h3 className="font-semibold text-gray-900">{t('dashboard.myExams')}</h3>
          <p className="text-gray-500 text-sm">{t('dashboard.myExamsDesc')}</p>
        </Link>
        <Link
          to="/my-teaching/promo-codes"
          className="block p-6 glass-card rounded-lg"
        >
          <div className="text-2xl mb-2">&#127991;</div>
          <h3 className="font-semibold text-gray-900">{t('dashboard.promoCodes')}</h3>
          <p className="text-gray-500 text-sm">{t('dashboard.promoCodesDesc')}</p>
        </Link>
        <Link
          to="/my-teaching/approvals"
          className="block p-6 glass-card rounded-lg"
        >
          <div className="text-2xl mb-2">&#9989;</div>
          <h3 className="font-semibold text-gray-900">{t('dashboard.approvalCenter')}</h3>
          <p className="text-gray-500 text-sm">{t('dashboard.approvalCenterDesc')}</p>
        </Link>
      </div>
    </div>
  );
}
